import { Link } from 'react-router-dom';
import ConsoleTable from './ConsoleTable.jsx';
import StatusWord from './StatusWord.jsx';
import { customerSectionPath } from '../lib/navigation.js';
import { timeAgo } from '../lib/customer-format.js';
import { fleetStatusRows } from '../../../shared/fleet-status.mjs';

// Dot tones per state (StatusWord: the color is in the dot only, the word says it). Unknown is never green.
const TRUST_TONE = Object.freeze({ current: 'good', outdated: 'warn', unknown: 'off', none: 'off' });
const COLLECTOR_TONE = Object.freeze({ current: 'good', behind: 'warn', failed: 'bad', 'in-progress': 'info', unknown: 'off', none: 'off' });
const RUN_TONE = Object.freeze({ healthy: 'good', partial: 'warn', 'timed-out': 'warn', failed: 'bad', none: 'off' });

// Sort by state, worst first, never by the label text (a label carries a version or a reason, not a rank).
const TRUST_RANK = Object.freeze({ outdated: 0, unknown: 1, none: 2, current: 3 });
const COLLECTOR_RANK = Object.freeze({ failed: 0, behind: 1, unknown: 2, 'in-progress': 3, none: 4, current: 5 });

const dateOf = (iso) => (iso ? new Date(iso).toLocaleDateString() : null);
const nameOf = (c) => c.companyName || c.customerId;

function Secondary({ children, testId }) {
  return <div className="mt-0.5 text-xs text-ipc-muted" data-testid={testId}>{children}</div>;
}

/**
 * Fleet status (operator fleet Overview): per customer, the trust stack, the collector and the last run, so the
 * operator sees who needs a push. Everything comes from GET /customers (no live call into a customer account).
 * @param {{ customers: object[], latestKey: string|null }} props
 */
export default function FleetStatusTable({ customers, latestKey }) {
  const rows = fleetStatusRows(customers, latestKey);
  const attention = rows.filter((r) => r.attention.length > 0).length;
  const columns = [
    {
      id: 'customer',
      header: 'Customer',
      sortValue: (r) => nameOf(r.customer).toLowerCase(),
      cell: (r) => (
        <div className="min-w-0">
          <Link to={customerSectionPath(r.customer.customerId, 'overview')} className="font-medium text-ipc-light hover:underline">
            {nameOf(r.customer)}
          </Link>
          <div className="font-mono text-xs text-ipc-muted">{r.customer.customerId}</div>
          {r.attention.length > 0 ? <Secondary testId="fleet-attention">Needs attention: {r.attention.join(', ')}</Secondary> : null}
        </div>
      ),
    },
    {
      id: 'trust',
      header: 'Trust stack',
      sortValue: (r) => TRUST_RANK[r.trust.state] ?? 99,
      cell: (r) => (
        <div>
          <StatusWord tone={TRUST_TONE[r.trust.state]} data-state={r.trust.state}>{r.trust.label}</StatusWord>
          {r.trust.checkedAt ? <Secondary>checked {timeAgo(r.trust.checkedAt)}</Secondary> : null}
        </div>
      ),
    },
    {
      id: 'collector',
      header: 'Collector',
      sortValue: (r) => COLLECTOR_RANK[r.collector.state] ?? 99,
      cell: (r) => (
        <div title={r.collector.deployedKey ? `Runs ${r.collector.deployedKey}` : undefined}>
          <StatusWord tone={COLLECTOR_TONE[r.collector.state]} data-state={r.collector.state}>{r.collector.label}</StatusWord>
          {r.collector.pushedAt ? <Secondary>last push {dateOf(r.collector.pushedAt)}</Secondary> : null}
        </div>
      ),
    },
    {
      id: 'run',
      header: 'Last run',
      sortValue: (r) => (r.run.at ? new Date(r.run.at).getTime() : null),
      cell: (r) => (
        <div>
          <StatusWord tone={RUN_TONE[r.run.state]} data-state={r.run.state}>{r.run.label}</StatusWord>
          {r.run.at ? <Secondary>{timeAgo(r.run.at)}</Secondary> : null}
          {r.run.collectorVersion ? <Secondary>ran collector {r.run.collectorVersion}</Secondary> : null}
        </div>
      ),
    },
  ];
  return (
    <ConsoleTable
      label="Fleet status"
      tableCaption="Fleet status: trust stack, collector and last run per customer"
      caption={latestKey ? null : 'The latest collector version is not known, so every collector shows Unknown.'}
      columns={columns}
      rows={rows}
      getRowId={(r) => r.customer.customerId}
      rowName={(r) => nameOf(r.customer)}
      searchText={(r) => `${nameOf(r.customer)} ${r.customer.customerId}`}
      searchPlaceholder="Search customers"
      filters={[{
        id: 'attention',
        label: 'Show',
        options: [{ value: 'attention', label: `Needs attention (${attention})` }],
        test: (r) => r.attention.length > 0,
      }]}
      emptyState={{ title: 'No customers to show', body: 'Nothing matches this view.' }}
      framed={false}
    />
  );
}
